import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import CreateMenu from './CreateMenu';
import CreateModal from './CreateModal';
import AdminPanel from './AdminPanel';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import AddOutlined from '@mui/icons-material/AddOutlined';
import { useWorkspace } from '../context/WorkspaceContext';
import CreateWorkspaceModal from './CreateWorkspaceModal';
import AddCompanyModal from './AddCompanyModal';
import AddPersonnelModal from './AddPersonnelModal';
import AddVenueModal from './AddVenueModal';

function getInitials(name) {
  return name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .slice(0, 2)
    .join('');
}

function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

function RightPanelItem({ icon, label, danger, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '11px 20px',
        color: danger ? '#BF2634' : (hovered ? '#39FF14' : '#F5F5F5'),
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '14px',
        cursor: 'pointer',
        backgroundColor: danger && hovered
          ? 'rgba(191, 38, 52, 0.08)'
          : (!danger && hovered ? 'rgba(57, 255, 20, 0.06)' : 'transparent'),
        transition: 'all 0.15s ease',
      }}
    >
      {icon}
      {label}
    </div>
  );
}

function CreateWorkspaceButton({ onClose, onOpen }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => {
        onClose();
        onOpen();
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        margin: '8px 20px 4px 20px',
        padding: '8px 14px',
        backgroundColor: hovered ? 'rgba(57, 255, 20, 0.08)' : 'transparent',
        border: '1px solid #39FF14',
        borderRadius: '4px',
        color: '#39FF14',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '13px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      <AddOutlined style={{ fontSize: 16, color: 'inherit' }} />
      Create Workspace
    </button>
  );
}

export default function TopBar({ hideHamburger = false }) {
  const { user, logout } = useAuth();
  const { workspaces, activeWorkspace, switchWorkspace } = useWorkspace();
  const { toggleMenu } = useUI();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hamburgerHover, setHamburgerHover] = useState(false);
  const [dashboardHover, setDashboardHover] = useState(false);
  const [createHover, setCreateHover] = useState(false);
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [createModalType, setCreateModalType] = useState(null);
  const [showCreateWorkspaceModal, setShowCreateWorkspaceModal] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [showAddPersonnelModal, setShowAddPersonnelModal] = useState(false);
  const [showAddVenueModal, setShowAddVenueModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [emailHover, setEmailHover] = useState(false);
  const [addSeatHover, setAddSeatHover] = useState(false);
  const [inviteHover, setInviteHover] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSignOut() {
    logout();
    navigate('/login');
  }

  const avatarLetter = user?.email ? user.email[0].toUpperCase() : '?';

  return (
    <>
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '56px',
        backgroundColor: '#1a1a1a',
        borderBottom: '1px solid #333333',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
      }}
    >
      {/* LEFT SECTION */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Hamburger button */}
        {!hideHamburger && (
          <div
            onClick={toggleMenu}
            onMouseEnter={() => setHamburgerHover(true)}
            onMouseLeave={() => setHamburgerHover(false)}
            style={{ cursor: 'pointer', padding: '4px' }}
          >
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                style={{
                  display: 'block',
                  width: '22px',
                  height: '2px',
                  backgroundColor: hamburgerHover ? '#39FF14' : '#F5F5F5',
                  margin: '4px 0',
                  transition: 'background-color 0.15s ease',
                }}
              />
            ))}
          </div>
        )}

        {/* Dashboard icon button */}
        {!hideHamburger && (
          <div
            onClick={() => navigate('/dashboard')}
            onMouseEnter={() => setDashboardHover(true)}
            onMouseLeave={() => setDashboardHover(false)}
            style={{
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '4px',
              color: dashboardHover ? '#39FF14' : '#F5F5F5',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 0.15s ease',
            }}
          >
            <DashboardOutlined style={{ fontSize: 20, color: 'inherit' }} />
          </div>
        )}

        {/* Create button + dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setIsCreateMenuOpen((prev) => !prev)}
            onMouseEnter={() => setCreateHover(true)}
            onMouseLeave={() => setCreateHover(false)}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            style={{
              backgroundColor: createHover ? '#8B1C26' : '#BF2634',
              color: '#FFFFFF',
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: '14px',
              padding: '8px 16px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
            }}
          >
            + Create
          </button>
          <CreateMenu
            isOpen={isCreateMenuOpen}
            onClose={() => setIsCreateMenuOpen(false)}
            onSelect={(key) => {
              setIsCreateMenuOpen(false);
              if (key === 'client')    { setShowAddClientModal(true); return; }
              if (key === 'vendor')    { setShowAddVendorModal(true); return; }
              if (key === 'venue')     { setShowAddVenueModal(true); return; }
              if (key === 'personnel') { setShowAddPersonnelModal(true); return; }
              if (key === 'talent')    { navigate('/talent'); return; }
              if (key === 'project')   { navigate('/projects'); return; }
              if (key === 'proposal')  { navigate('/proposals'); return; }
              if (key === 'event')     { navigate('/events'); return; }
              if (key === 'invite')    { console.log('invite user'); return; }
              if (key === 'team')      { console.log('create team'); return; }
              setCreateModalType(key);
            }}
          />
        </div>
      </div>

      {/* CENTER */}
      {(!hideHamburger && activeWorkspace) ? (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 700,
              fontSize: '18px',
              color: '#FFFFFF',
              letterSpacing: '1px',
              maxWidth: '300px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {activeWorkspace.name}
          </div>
          <div
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: '10px',
              color: '#39FF14',
              letterSpacing: '3px',
              marginTop: '2px',
              textShadow:
                '0 0 6px rgba(57, 255, 20, 0.8), 0 0 12px rgba(57, 255, 20, 0.4)',
            }}
          >
            Powered by VANCE
          </div>
        </div>
      ) : (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 900,
            fontSize: '28px',
            letterSpacing: '10px',
            color: '#39FF14',
            textShadow:
              '0 0 6px rgba(57, 255, 20, 0.9), 0 0 12px rgba(57, 255, 20, 0.6), 0 0 20px rgba(57, 255, 20, 0.3)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          VANCE
        </div>
      )}

      {/* RIGHT SECTION */}
      <div style={{ position: 'relative' }} ref={dropdownRef}>
        {/* Avatar */}
        <div
          onClick={() => setDropdownOpen((prev) => !prev)}
          style={{
            width: '36px',
            height: '36px',
            backgroundColor: '#565656',
            border: '2px solid #39FF14',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 700,
            fontSize: '16px',
            color: '#F5F5F5',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {avatarLetter}
        </div>

        {/* Two-panel dropdown */}
        {dropdownOpen && (
          <div
            style={{
              position: 'absolute',
              top: '48px',
              right: 0,
              backgroundColor: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              boxShadow: '0 16px 48px rgba(0,0,0,0.6), 0 0 40px rgba(57, 255, 20, 0.06)',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'row',
              minWidth: '480px',
              overflow: 'hidden',
            }}
          >
            {/* LEFT PANEL */}
            <div
              style={{
                width: '280px',
                borderRight: '1px solid #3a3a3a',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* User section */}
              <div style={{ padding: '20px 20px 16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: '#565656',
                      border: '2px solid #39FF14',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 700,
                      fontSize: '18px',
                      color: '#F5F5F5',
                      flexShrink: 0,
                    }}
                  >
                    {avatarLetter}
                  </div>
                  <div
                    onMouseEnter={() => setEmailHover(true)}
                    onMouseLeave={() => setEmailHover(false)}
                    onClick={() => { setDropdownOpen(false); navigate('/profile'); }}
                    style={{
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 400,
                      fontSize: '14px',
                      color: emailHover ? '#39FF14' : '#F5F5F5',
                      cursor: 'pointer',
                      transition: 'color 0.15s ease',
                      wordBreak: 'break-all',
                    }}
                  >
                    {user?.email || ''}
                  </div>
                </div>
              </div>

              <div style={{ borderTop: '1px solid #3a3a3a', margin: 0 }} />

              {/* Workspace section */}
              <div style={{ padding: '16px 0 8px 0' }}>
                <div
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 500,
                    fontSize: '11px',
                    color: '#565656',
                    letterSpacing: '2px',
                    padding: '0 20px 8px 20px',
                  }}
                >
                  WORKSPACE
                </div>

                {workspaces.map((ws) => {
                  const initials = getInitials(ws.name);
                  const rgbColor = hexToRgb(ws.color || '#BF2634');
                  const isActive = activeWorkspace?._id === ws._id;
                  return (
                    <div
                      key={ws._id}
                      onClick={() => switchWorkspace(ws)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: isActive ? '10px 20px 10px 17px' : '10px 20px',
                        cursor: 'pointer',
                        backgroundColor: isActive ? 'rgba(57, 255, 20, 0.08)' : 'transparent',
                        borderLeft: isActive ? '3px solid #39FF14' : '3px solid transparent',
                        boxShadow: isActive ? 'inset 0 0 20px rgba(57, 255, 20, 0.04)' : 'none',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {/* Workspace identifier */}
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          ...(ws.logo
                            ? {}
                            : {
                                backgroundColor: ws.color || '#BF2634',
                                boxShadow: `0 0 10px rgba(${rgbColor}, 0.6)`,
                              }),
                        }}
                      >
                        {ws.logo ? (
                          <img
                            src={ws.logo}
                            alt={ws.name}
                            style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                          />
                        ) : (
                          <span
                            style={{
                              fontFamily: "'Roboto', sans-serif",
                              fontWeight: 700,
                              fontSize: '13px',
                              color: '#FFFFFF',
                            }}
                          >
                            {initials}
                          </span>
                        )}
                      </div>

                      <span
                        style={{
                          fontFamily: "'Roboto', sans-serif",
                          fontWeight: 500,
                          fontSize: '14px',
                          color: '#F5F5F5',
                          flex: 1,
                        }}
                      >
                        {ws.name}
                      </span>
                    </div>
                  );
                })}
              </div>

              <CreateWorkspaceButton
                onClose={() => setDropdownOpen(false)}
                onOpen={() => setShowCreateWorkspaceModal(true)}
              />

              <div style={{ borderTop: '1px solid #3a3a3a', margin: '8px 0 0 0' }} />

              {/* Bottom actions */}
              <div
                style={{
                  padding: '12px 20px 16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <button
                  onMouseEnter={() => setAddSeatHover(true)}
                  onMouseLeave={() => setAddSeatHover(false)}
                  style={{
                    backgroundColor: addSeatHover ? 'rgba(229, 192, 75, 0.1)' : 'transparent',
                    border: '1px solid #E5C04B',
                    color: '#E5C04B',
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 500,
                    fontSize: '13px',
                    padding: '7px 14px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  Add Seat
                </button>
                <button
                  onMouseEnter={() => setInviteHover(true)}
                  onMouseLeave={() => setInviteHover(false)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: inviteHover ? '#39FF14' : '#F5F5F5',
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 500,
                    fontSize: '13px',
                    padding: '7px 0',
                    cursor: 'pointer',
                    transition: 'color 0.15s ease',
                  }}
                >
                  + Invite User
                </button>
              </div>
            </div>

            {/* RIGHT PANEL */}
            <div
              style={{
                width: '200px',
                padding: '20px 0',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <RightPanelItem icon={<TuneOutlined style={{ fontSize: 18, color: 'inherit' }} />} label="Admin" onClick={() => { setDropdownOpen(false); setShowAdminPanel(true); }} />
                <RightPanelItem icon={<SettingsOutlined style={{ fontSize: 18, color: 'inherit' }} />} label="Settings" onClick={() => setDropdownOpen(false)} />
              </div>
              <div>
                <div style={{ borderTop: '1px solid #3a3a3a', margin: '8px 20px' }} />
                <RightPanelItem icon={<LogoutOutlined style={{ fontSize: 18, color: 'inherit' }} />} label="Log Out" danger onClick={handleSignOut} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    <CreateModal
      isOpen={createModalType !== null}
      type={createModalType}
      onClose={() => setCreateModalType(null)}
    />
    <CreateWorkspaceModal
      isOpen={showCreateWorkspaceModal}
      onClose={() => setShowCreateWorkspaceModal(false)}
      onSuccess={() => setShowCreateWorkspaceModal(false)}
    />
    <AddCompanyModal
      isOpen={showAddClientModal}
      onClose={() => setShowAddClientModal(false)}
      onSuccess={() => setShowAddClientModal(false)}
      type="client"
    />
    <AddCompanyModal
      isOpen={showAddVendorModal}
      onClose={() => setShowAddVendorModal(false)}
      onSuccess={() => setShowAddVendorModal(false)}
      type="vendor"
    />
    <AddPersonnelModal
      isOpen={showAddPersonnelModal}
      onClose={() => setShowAddPersonnelModal(false)}
      onSuccess={() => setShowAddPersonnelModal(false)}
    />
    <AddVenueModal
      isOpen={showAddVenueModal}
      onClose={() => setShowAddVenueModal(false)}
      onSuccess={(venue) => { setShowAddVenueModal(false); navigate('/venues'); }}
    />
    <AdminPanel
      isOpen={showAdminPanel}
      onClose={() => setShowAdminPanel(false)}
    />
    </>
  );
}
